import React from 'react';
import { connect } from 'react-redux';

import withStyles from '@mui/styles/withStyles';

import { setApiResponseInfo } from '../store/slices/apiResponseInfoSlice';

import { constructPhoneRolesString, showAlert } from '../utils/utils';

import DA from '../store/ReduxAccess';
import FormProcessor from '../common/FormProcessor';

import Modal from './Modal';
import NewOrgContactPanel from './NewOrgContactPanel';
import AlertModal from './AlertModal';
import CrmButton from './CrmButton';

const localStyles = (theme) => ({
  newReferrerOrgModal: {
    maxWidth: '600px',
    minWidth: '600px',
    [theme.breakpoints.down('md')]: {
      top: 260,
    },
  },
});

/*
 * Call with something like:
  <IcfUnaffiliatedContactModal
    onSuccess={(contactId) => this.handleNewUnaffilContactSuccess(contactId)}
    onCancel={() => this.cancelContactModal()}
  />
 */

class IcfUnaffiliatedContactModal extends React.Component {
  constructor(props) {
    super(props);

    this.state = {
      errorMessage: undefined,
      contactName: '',
      contactJobTitle: '',
      contactDescription: '',
      contactPhone1Roles: '',
      contactPhone1Number: '',
      contactPhone2Roles: '',
      contactPhone2Number: '',
      contactEmail: '',
      contactOwnerId: DA.getLoggedInUserPersonId(),
      contactTemperature: 'Unknown',
      shouldShowAlertModal: false,
      shouldShowConfirmDuplicationUI: false,
      alertModalText: '',
    };
  }

  validationPolicy = {
    requireFirmName: false,
    requireFirmType: false,
    requireAddress: false,
    forbidPartialAddress: true,
    allowZipFreeAddress: true,
    requireContact: true,
    requireJobTitle: false,
    requireContactDescription: false,
    requireContactPhone: false,
    requireContactEmail: false,
    requireAtLeaseOneOfContactPhoneOrEmail: true,
  };

  cleanState = {
    errorMessage: undefined,
    contactName: '',
    contactJobTitle: '',
    contactDescription: '',
    contactPhone1Roles: '',
    contactPhone1Number: '',
    contactPhone2Roles: '',
    contactPhone2Number: '',
    contactEmail: '',
    contactOwnerId: DA.getLoggedInUserPersonId(),
    contactTemperature: 'Unknown',
    shouldShowAlertModal: false,
    shouldShowConfirmDuplicationUI: false,
  };

  allPossiblePhoneRoles = ['Mobile', 'Business', 'Home', 'Work', 'Fax'];
  possibleContactPhoneRoles = ['Work', 'Mobile', 'Home'];

  componentDidMount() {
    this.setState({ contactOwnerId: DA.getLoggedInUserPersonId() });
  }

  erase() {
    this.setState({ ...this.cleanState });
  }

  onSuccessfulSubmission(contactId) {
    this.erase();
    this.props.onSuccess(contactId);
  }

  handleTextChange(event) {
    const rawValue = event.target.value;
    const value = event.target.id === 'contactEmail' ? rawValue.trim() : rawValue;
    this.setState({ [event.target.id]: value });
  }

  handleCompanyStateChange(newValue) {
    this.setState({ state: newValue });
  }

  handleOkClick() {
    this.validateAndSubmit();
  }

  validateAndSubmit() {
    const validation = this.validateForm();
    if (validation.valid) {
      this.submit();
    } else {
      showAlert(this, validation.errorMessage);
    }
  }

  validateForm() {
    return FormProcessor.validateForm(this.state, this.validationPolicy);
  }

  submit() {
    FormProcessor.submitUnafilliatedContactForm(this);
  }

  cancelForm() {
    this.props.onCancel({ action: 'canceled' });
  }

  handleContactPhone1RolesChange(rolesObj) {
    const newPhoneRoles = constructPhoneRolesString(rolesObj, this.allPossiblePhoneRoles);
    this.setState({ contactPhone1Roles: newPhoneRoles });
  }

  handleContactPhone1NumberChange(newNumber) {
    this.setState({ contactPhone1Number: newNumber });
  }

  handleContactPhone2RolesChange(rolesObj) {
    const newPhoneRoles = constructPhoneRolesString(rolesObj, this.allPossiblePhoneRoles);
    this.setState({ contactPhone2Roles: newPhoneRoles });
  }

  handleContactPhone2NumberChange(newNumber) {
    this.setState({ contactPhone2Number: newNumber });
  }

  // Called by FormProcessor in case of suspected duplicate contact
  showConfirmDuplicateUI(firmContactApiStruct) {
    this.firmContactApiStruct = firmContactApiStruct;
    this.setState({ shouldShowConfirmDuplicationUI: true });
  }

  confirmCreateDuplicateFirmContact() {
    this.setState({ shouldShowConfirmDuplicationUI: false });
    FormProcessor.acceptFirmContact(this, this.firmContactApiStruct);
  }

  cancelCreateDuplicateFirmContact() {
    this.setState({ shouldShowConfirmDuplicationUI: false });
  }

  render() {
    const { classes } = this.props;
    const { shouldShowAlertModal, alertModalText } = this.state;
    const title = 'Register Unaffiliated Contact';
    return (
      <div>
        <AlertModal
          show={shouldShowAlertModal}
          title="Alert"
          text={alertModalText}
          closeFunc={() => this.setState({ shouldShowAlertModal: false })}
        />

        <ConfirmContactDuplicationModal
          show={this.state.shouldShowConfirmDuplicationUI}
          firmContactApiStruct={this.firmContactApiStruct}
          createDuplicateFunc={() => this.confirmCreateDuplicateFirmContact()}
          cancelFunc={() => this.cancelCreateDuplicateFirmContact()}
        />

        <Modal
          classes={{ modal: classes.newReferrerOrgModal }}
          open={true}
          title={title}
          okButtonLabel="Register"
          cancelButtonLabel="Cancel"
          onOk={() => this.handleOkClick()}
          onCancel={() => this.cancelForm()}
        >
          <NewOrgContactPanel ownerModal={this} personLabel={'Contact'} />
        </Modal>
      </div>
    );
  }
}

const RawConfirmContactDuplicationModal = ({
  classes,
  show,
  firmContactApiStruct,
  cancelFunc,
  createDuplicateFunc,
}) => {
  if (!show) {
    return null;
  }
  return (
    <Modal open={show} title="Really Create Duplicate Contact?" hideButtons={true} onCancel={cancelFunc}>
      <div>We already have a Contact with these identifying properties.</div>
      <div className={classes.div2}>Please choose to</div>
      <div className={classes.buttonDiv}>
        <CrmButton outlined size="small" onClick={() => createDuplicateFunc()}>
          Create a Second One (rather unlikely)
        </CrmButton>
        <CrmButton className={classes.cancelButton} filled size="small" onClick={() => cancelFunc()}>
          Cancel
        </CrmButton>
      </div>
    </Modal>
  );
};

const duplicateContactModalStyles = {
  div2: {
    marginTop: '10px',
  },
  buttonDiv: {
    marginTop: '12px',
    textAlign: 'right',
  },
  cancelButton: {
    marginLeft: '12px',
  },
};

const ConfirmContactDuplicationModal = withStyles(duplicateContactModalStyles)(RawConfirmContactDuplicationModal);

const mapStateToProps = (state) => ({});

const actionProducers = {
  setApiResponseInfo,
};

export default connect(mapStateToProps, actionProducers)(withStyles(localStyles)(IcfUnaffiliatedContactModal));
